<template>
  <app-page-container :custom="1600" :min-height="0">
    <div class="ui-page">
      <app-filter-toolbar controls-align="start">
        <template #primary>
          <app-input id="honeymoon-search" v-model="search" design="new" type="search" placeholder="Safari, lead or group" aria-label="Search" @input.native="onSearch" />
        </template>
        <app-date-picker v-model="dateFrom" design="new" :input-attr="{ id: 'honeymoon-date-from', 'aria-label': 'Tour start from' }" placeholder="Tour start from" />
        <app-date-picker v-model="dateTo" design="new" :input-attr="{ id: 'honeymoon-date-to', 'aria-label': 'Tour start to' }" placeholder="Tour start to" />
        <template #selection>
          <app-filters v-model="period" design="new" refined weight="medium" :filters="periodOptions" name="honeymoon-period" aria-label="Safari period" />
        </template>
        <template #secondary-selection>
          <app-filters v-model="completion" design="new" refined weight="medium" :filters="completionOptions" name="honeymoon-completion" aria-label="Completion" />
        </template>
        <template #tertiary-selection>
          <app-filters v-model="currentStep" design="new" refined weight="medium" wrap :filters="stepFilters" name="honeymoon-step" aria-label="Current checklist step" :aria-busy="String(loading)" />
        </template>
        <template v-if="hasFilters" #clear>
          <AppClearFiltersButton design="new" icon-only @clear="clearFilters" />
        </template>
      </app-filter-toolbar>

      <AppContentSkeleton v-if="loading" label="Loading Honeymoon reservations" :columns="columns" min-width="1200px" />
      <AppErrorState v-else-if="error" :message="error" @retry="fetchRows" />
      <template v-else>
        <div v-if="rows.length" class="ui-stack">
          <AppCard
            v-for="group in rowGroups"
            :key="group.value"
            design="new"
            variant="table"
            header-density="compact"
            :title="period === 'upcoming' || currentStep === 'all' ? group.label : ''"
            :count="period === 'upcoming' || currentStep === 'all' ? group.rows.length : null"
          >
            <AppDataTable design="new" density="dense" :rows="group.rows" :columns="columns" min-width="1200px">
              <template #cell-safari="{ row }">
                <AppTableText :text="row.safari_group && row.safari_group.name">
                  <router-link :to="{ name: 'reservation-safari-detail', params: { id: row.id } }" class="ui-link">{{ row.safari_group ? row.safari_group.name : '—' }}</router-link>
                </AppTableText>
              </template>
              <template #cell-tour_start="{ row }"><AppTableText :text="dateFormat(row.tour_start)" /></template>
              <template #cell-tour_end="{ row }"><AppTableText :text="dateFormat(row.tour_end)" /></template>
              <template v-for="step in checklistSteps" v-slot:[`cell-${step.value}`]="{ row }">
                <AppStatusBadge
                  :key="step.value"
                  design="new"
                  compact
                  :tone="stepDone(row, step.value) ? 'green' : 'amber'"
                  :label="stepDone(row, step.value) ? 'Yes' : 'No'"
                  :aria-label="step.label + ': ' + (stepDone(row, step.value) ? 'Yes' : 'No')"
                />
              </template>
            </AppDataTable>
          </AppCard>
        </div>
        <app-no-results v-if="!rows.length" design="new" title="No Honeymoon reservations found" text="" />
        <AppPagination
          design="new"
          placement="embedded"
          :page="pagination.page"
          :pages="pagination.pages"
          :count="pagination.count"
          :visible-count="rows.length"
          :page-size="pageSize"
          :loading="loading"
          @change="onPage"
        />
      </template>
    </div>
  </app-page-container>
</template>

<script>
import debounce from 'lodash/debounce'
import { format, parseISO } from 'date-fns'
import { getSafariHoneymooners } from '@/http'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'

const CHECKLIST_STEPS = [
  { value: 'generated', field: 'generated', label: 'Generate wheel cover' },
  { value: 'sent_for_printing', field: 'sent_for_printing', label: 'Sent for printing' },
  { value: 'printed', field: 'printed', label: 'Printed and received at the office' },
  { value: 'complimentary_wine_added', field: 'complimentary_wine_added', label: 'Complimentary wine added' },
  { value: 'completed', field: 'completed', label: 'Passed to the driver' },
]

export default {
  name: 'SafariScheduleHoneymooners',
  components: { AppContentSkeleton, AppErrorState, AppTableText },
  data() {
    return {
      error: '',
      loading: false,
      rows: [],
      summary: {},
      summaryLoaded: false,
      search: '',
      dateFrom: null,
      dateTo: null,
      requestId: 0,
      resettingFilters: false,
      period: 'upcoming',
      completion: '',
      currentStep: 'all',
      pagination: { page: 1, pages: 1, count: 0 },
      periodOptions: [
        { value: 'upcoming', label: 'Upcoming' },
        { value: 'archive', label: 'Archive' },
        { value: 'all', label: 'All' },
      ],
      completionOptions: [
        { value: '', label: 'All' },
        { value: 'incomplete', label: 'Incomplete' },
        { value: 'completed', label: 'Completed' },
      ],

    }
  },
  computed: {
    hasFilters() {
      return Boolean(this.search || this.dateFrom || this.dateTo || this.period !== 'upcoming' || this.completion || this.currentStep !== 'all')
    },
    columns() {
      return [
        { key: 'safari', label: 'Safari', width: '10%' },
        { key: 'tour_start', label: 'Tour Start', width: '8.5%' },
        { key: 'tour_end', label: 'Tour Finish', width: '8.5%' },
        ...CHECKLIST_STEPS.map((step, index) => ({ key: step.value, label: step.label, align: 'center', width: ['13%', '11%', '19%', '16%', '14%'][index] })),
      ]
    },
    stepFilters() {
      return this.stepFilterOptions.map(step => ({
        ...step,
        label: `${step.label} (${this.summaryLoaded ? this.stepCount(step.value) : this.error ? '—' : '…'})`,
      }))
    },
    pageSize() {
      return 50
    },
    checklistSteps() {
      return CHECKLIST_STEPS
    },
    summarySteps() {
      return [
        ...CHECKLIST_STEPS,
        { value: 'complete', label: 'Completed' },
      ]
    },
    stepFilterOptions() {
      return [
        { value: 'all', label: 'All' },
        ...this.summarySteps,
      ]
    },
    rowGroups() {
      if (this.period === 'upcoming') {
        const groups = new Map()
        this.rows.forEach(row => {
          const date = row.tour_start || ''
          if (!groups.has(date)) groups.set(date, { value: date, label: date ? this.dateFormat(date) : 'Tour start not set', rows: [] })
          groups.get(date).rows.push(row)
        })
        return [...groups.values()].sort((a, b) => (a.value || '9999').localeCompare(b.value || '9999'))
      }
      if (this.currentStep !== 'all') {
        return [
          {
            value: this.currentStep,
            label: this.stepLabel(this.currentStep),
            rows: this.rows,
          },
        ]
      }
      return this.summarySteps
        .map(step => ({
          ...step,
          rows: this.rows.filter(row => row.current_step === step.value),
        }))
        .filter(group => group.rows.length)
    },
  },
  watch: {
    dateFrom() {
      this.reload()
    },
    dateTo() {
      this.reload()
    },
    period() {
      this.reload()
    },
    completion() {
      this.reload()
    },
    currentStep() {
      this.reload()
    },
  },
  created() {
    this.onSearch = debounce(this.reload, 350)
    this.fetchRows()
  },
  beforeDestroy() {
    this.onSearch.cancel()
    this.requestId += 1
  },
  methods: {
    clearFilters() {
      this.onSearch.cancel()
      this.resettingFilters = true
      this.search = ''
      this.period = 'upcoming'
      this.completion = ''
      this.currentStep = 'all'
      this.dateFrom = null
      this.dateTo = null
      this.$nextTick(() => {
        this.resettingFilters = false
        this.reload()
      })
    },
    onSearch() {
      this.reload()
    },
    reload() {
      if (this.resettingFilters) return
      this.pagination.page = 1
      this.fetchRows()
    },
    onPage(page) {
      this.pagination.page = page
      this.fetchRows()
    },
    stepCount(step) {
      if (step === 'all') {
        return this.summarySteps.reduce((total, item) => total + (this.summary[item.value] || 0), 0)
      }
      return this.summary[step] || 0
    },
    fetchRows() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      return getSafariHoneymooners({
        period: this.period,
        search: (this.search || '').trim(),
        completion: this.completion,
        tour_start__gte: this.dateFrom,
        tour_start__lte: this.dateTo,
        current_step: this.currentStep === 'all' ? null : this.currentStep,
        page: this.pagination.page,
        page_size: this.pageSize,
      })
        .then(response => {
          if (requestId !== this.requestId) return
          this.rows = response.data.results
          this.summary = response.data.summary
          this.summaryLoaded = true
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
        })
        .catch(() => {
          if (requestId !== this.requestId) return
          this.error = 'Unable to load Honeymoon reservations. Please try again.'
        })
        .finally(() => {
          if (requestId !== this.requestId) return
          this.loading = false
        })
    },
    stepDone(row, step) {
      const definition = CHECKLIST_STEPS.find(item => item.value === step)
      return Boolean(definition && row.honeymoon_document && row.honeymoon_document[definition.field])
    },
    stepLabel(step) {
      const definition = this.summarySteps.find(item => item.value === step)
      return definition ? definition.label : 'Generate wheel cover'
    },
    dateFormat(value) {
      return value ? format(parseISO(value), 'dd MMM yyyy') : '—'
    },
  },
}
</script>
